import { makeAutoObservable } from "mobx";
import { ZEntityError } from "src/business/error/ZEntityError";
import { ZSpecificationTyped } from "src/business/specification";
import { mdmDeepEqual } from "src/common/mdmDeepEqual";
import { RemoteData } from "src/common/RemoteData";
import { isSystemKey } from "src/common/systemKey";
import { MfErrorsListStore } from "src/components/MForm/components/MfErrorsListView";
import { MFormStore } from "src/components/MForm/MFormStore";

import { extractClassIdsFromFieldsValue } from "./extractClassIdsFromFieldsValue";
import { RecordFormInfo } from "./makeRecordForm";

type SchemeData = {
  schemes: ZSpecificationTyped[];
};

export type CDSFormStoreLoader<TContext = unknown> = {
  load(
    context: TContext,
    currentValues?: unknown,
  ): Promise<{
    info: RecordFormInfo;
    schemes: ZSpecificationTyped[];
    errors?: ZEntityError[];
  }>;
};

const cloneValue = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const sanitizePersistedValues = (values: unknown): unknown => {
  if (typeof values !== "object" || values === null) {
    return values;
  }
  const copy = cloneValue(values as Record<string, unknown>);
  Object.keys(copy)
    .filter(isSystemKey)
    .forEach((key) => delete copy[key]);
  return copy;
};

const getRealignKey = (values: unknown): string =>
  extractClassIdsFromFieldsValue(values as Record<string, unknown>)
    .sort()
    .join(",");

export class CDSFormStore<TContext = unknown> {
  readonly form: MFormStore;

  readonly errorsStore: MfErrorsListStore;

  data: RemoteData<SchemeData> = { status: "none" };

  prevValues: unknown;

  pending = false;

  private lastRealignKey: string | null = null;

  constructor(
    private loader: CDSFormStoreLoader<TContext>,
    private context: TContext,
  ) {
    this.form = new MFormStore({
      onChange: () => this.handleFormChange(),
    });
    this.errorsStore = new MfErrorsListStore(this.form);
    makeAutoObservable(this);
    this.init();
  }

  get schemes() {
    const { data } = this;
    if (data.status !== "ready") return [];
    return data.result.schemes;
  }

  setData(newData: RemoteData<SchemeData>) {
    this.data = newData;
  }

  setPrevValues(values: unknown) {
    this.setFullPrevValues(values);
    this.prevValues = sanitizePersistedValues(values);
    this.checkChanged();
  }

  setPending(flag: boolean) {
    this.pending = flag;
  }

  setContext(context: TContext) {
    this.context = context;
  }

  lock() {
    this.setPending(true);
    this.form.root.setDisabled(true);
  }

  unlock() {
    this.setPending(false);
    this.form.root.setDisabled(false);
  }

  async init() {
    try {
      this.setData({ status: "wait" });
      this.lock();
      const { info, schemes, errors } = await this.loader.load(this.context);
      this.applyLoadedInfo(info, schemes, errors ?? []);
    } catch (error) {
      this.setError(error);
    } finally {
      this.unlock();
    }
  }

  async reload() {
    try {
      this.lock();
      const { info, schemes, errors } = await this.loader.load(this.context);
      this.applyLoadedInfo(info, schemes, errors ?? []);
    } catch (error) {
      this.setError(error);
    } finally {
      this.unlock();
    }
  }

  async realign() {
    const { data, form, prevValues } = this;
    if (data.status !== "ready") {
      return;
    }

    this.lock();
    try {
      const { info, schemes, errors } = await this.loader.load(
        this.context,
        form.fieldsValue,
      );
      const { data: nextData } = info;
      const mergedVals = {
        ...(nextData as Record<string, unknown>),
        ...(prevValues as Record<string, unknown>),
        ...(form.fieldsValue as Record<string, unknown>),
      };

      this.applyFormInfo(info, mergedVals, errors ?? []);
      this.syncRealignKey(mergedVals);
      this.setData({
        status: "ready",
        result: {
          schemes,
        },
      });
    } catch (error) {
      this.setError(error);
    } finally {
      this.unlock();
    }
  }

  private applyFormInfo(
    info: RecordFormInfo,
    values: unknown,
    externalErrors: ZEntityError[],
  ) {
    this.form.initStd(info.meta, values, externalErrors);
  }

  private applyLoadedInfo(
    info: RecordFormInfo,
    schemes: ZSpecificationTyped[],
    externalErrors: ZEntityError[],
  ) {
    const { data } = info;

    this.applyFormInfo(info, data, externalErrors);
    this.syncRealignKey(data);
    this.setPrevValues(this.form.fieldsValue);
    this.setData({
      status: "ready",
      result: {
        schemes,
      },
    });
  }

  private setError(error: unknown) {
    this.setData({ status: "error", error });
  }

  private syncRealignKey(values: unknown) {
    this.lastRealignKey = getRealignKey(values);
  }

  private handleFormChange() {
    this.checkChanged();

    if (this.pending || this.data.status !== "ready") {
      return;
    }

    const nextKey = getRealignKey(this.form.fieldsValue);
    if (this.lastRealignKey === nextKey) {
      return;
    }

    this.lastRealignKey = nextKey;
    this.realign().catch((error) => this.setError(error));
  }

  // 1. К сожалению, не получилось использовать formStore.isChanged
  //    Потому что на форме есть элементы, который перестраивает метаданные. (Блок "Классификация")
  //    А механизм isChange реализован через дерево метаданных.
  //    Т.е. предполагается, что они не меняются в процессе редактирования данных.
  // 2. Не получилось использовать prevValues, т.к. из них исключены системные данные.
  //    А нам необходимо учитывать изменение системного поля _cls (см. MDM2-749)
  //    Поэтому появились fullPrevValues
  get isChanged(): boolean {
    // Не получилось вычислять этот признак, т.к. setPrevValues не приводит к обновлению значения (видимо, вызывается не из обсервера)
    return this.changed;
  }

  private changed = false;

  setAsUnchanged() {
    this.changed = false;
  }

  checkChanged() {
    const curValues = cleanData(this.form.fieldsValue);
    const prevValues = cleanData(this.fullPrevValues);
    this.changed = !mdmDeepEqual(curValues, prevValues);
  }

  fullPrevValues: unknown = null;

  setFullPrevValues(values: unknown) {
    this.fullPrevValues = cleanData(values); // deep clone
  }
}

// Очистка от проксей
// PS. toJS (mobx) не помогает, т.к. оно не рекурсивное - вложенные объекты остаются Proxy
const cleanData = (data: unknown): unknown => JSON.parse(JSON.stringify(data));
